import * as Yup from "yup";
import { EnabledChannels, LeadDetails } from "../../../utils/types";
import { FieldConfig, FormValues } from "../utils/types";
import { CountryOption } from "../../../../../components/PhoneNumberCountryCodeSelector/utils/types";
import {
  findCountryByCode,
} from "../../../../../components/PhoneNumberCountryCodeSelector/utils/phoneNumberUtils";
import { getRegionFlag } from "../../../../../utils/constants/regionDefaultCallingCodes";

export const formatDate = (dateString: string) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "2-digit",
  });
};

export const buildToggleStates = (
  channels: string[],
  enabledChannels?: EnabledChannels
) =>
  Object?.fromEntries(
    channels?.map((key) => [
      key,
      enabledChannels?.[key as keyof EnabledChannels] ?? false,
    ])
  ) as EnabledChannels;

/**
 * Sorts fields by their display_order property in ascending order
 */
export const getSortedFields = (fields: FieldConfig[]): FieldConfig[] => {
  return [...fields].sort((a, b) => a.display_order - b.display_order);
};

/**
 * A field is treated as required when the backend marks it mandatory either
 * explicitly (`is_mandatory`) or by default (`is_mandatory_by_default`).
 * Single source of truth for the red "*" indicator and the Save-button gate.
 */
export const isFieldRequired = (field: FieldConfig): boolean =>
  !!field.is_mandatory || !!field.is_mandatory_by_default;

/**
 * Maps backend field keys to formik form field names
 */
export const mapFieldKeyToFormikName = (fieldKey: string): string => {
  const fieldKeyMap: { [key: string]: string } = {
    phone: "phoneNumber",
    whatsapp_phone: "whatsappNumber",
    "demographics.destination_country": "destinationCountry",
    "demographics.destination_city": "destinationCity",
    "demographics.university": "university",
    "demographics.source_country": "sourceCountry",
    move_in_session: "moveInSession",
    academic_year: "moveInSession",
    level_of_study: "levelOfStudy",
    tenant_type: "tenantType",
    course_type: "courseType",
    move_in_date: "move_in_date",
    room_type: "roomType",
    budget: "budget",
    min_budget: "min_budget",
    max_budget: "max_budget",
    stay_duration: "stay_duration",
    utm_source: "utmSource",
    utm_medium: "utmMedium",
    choosen_props: "choosenProps",
    tags: "tags",
    current_stage: "currentStage",
    how_did_you_hear_about_us: "howDidYouHearAboutUs",
    marketing_consent: "marketingConsent",
  };

  return fieldKeyMap[fieldKey] || fieldKey;
};

/**
 * Creates dynamic validation schema based on field configuration
 * For VF Admin in edit mode: All fields are nullable
 * For regular users: Respects is_mandatory flag from config
 */
export const createDynamicValidationSchema = (
  fields: FieldConfig[],
  isEdit: boolean,
  isVfAdmin: boolean,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): any => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const shape: any = {};

  // VF Admin in edit mode - skip validations, allow all fields as nullable
  if (isEdit && isVfAdmin) {
    fields.forEach((field) => {
      const formikFieldName = mapFieldKeyToFormikName(field.field_key);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let validator: any;

      if (field.allows_multiple) {
        validator = Yup.array().nullable();
      } else {
        validator = Yup.mixed().nullable();
      }

      shape[formikFieldName] = validator;
    });

    return Yup.object().shape(shape);
  }

  // Regular validation logic
  fields.forEach((field) => {
    // Skip budget - handled separately with min_budget/max_budget
    if (field.field_key === "budget") return;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let validator: any;

    // Check if field allows multiple values first
    if (field.allows_multiple) {
      validator = Yup.array();
      if (isFieldRequired(field)) {
        validator = validator.min(1, `${field.display_name} is required`);
      } else {
        validator = validator.nullable();
      }
    } else {
      // Single value fields
      switch (field.field_data_type) {
        case "string":
        case "enum":
          validator = Yup.string();
          break;
        case "email":
          validator = Yup.string().email("Invalid email format");
          break;
        case "number":
          validator = Yup.number().typeError("Must be a number");
          break;
        case "date":
          validator = Yup.date();
          break;
        case "phone":
          validator = Yup.string();
          break;
        case "object":
          validator = Yup.mixed();
          break;
        default:
          validator = Yup.mixed();
      }

      // level_of_study: only required when tenantType is student
      if (field.field_key === "level_of_study" && isFieldRequired(field)) {
        validator = Yup.string()
          .nullable()
          .when("tenantType", ([tenantType], schema) => {
            if (tenantType && tenantType !== "working professional") {
              return schema.required(`${field.display_name} is required`);
            }
            return schema.nullable();
          });
      } else if (isFieldRequired(field)) {
        validator = validator
          .typeError(`${field.display_name} is required`)
          .required(`${field.display_name} is required`);
      } else {
        validator = validator.nullable();
      }
    }

    // Map field_key to formik field name
    const formikFieldName = mapFieldKeyToFormikName(field.field_key);
    shape[formikFieldName] = validator;
  });

  // Check if budget field is mandatory from config
  const budgetField = fields.find(f => f.field_key === "budget");
  const isBudgetRequired = budgetField ? isFieldRequired(budgetField) : false;

  // Add custom budget validation
  if (isBudgetRequired) {
    shape.min_budget = Yup.string()
      .required("Min budget is required")
      .test(
        "min-budget-validation",
        "Min budget cannot be greater than max budget",
        function (value) {
          const { max_budget } = this.parent;
          if (!value || !max_budget) return true;
          const minBudget = parseFloat(value);
          const maxBudget = parseFloat(max_budget);
          if (isNaN(minBudget) || isNaN(maxBudget)) return true;
          return minBudget <= maxBudget;
        }
      );

    shape.max_budget = Yup.string()
      .required("Max budget is required")
      .test(
        "max-budget-validation",
        "Max budget cannot be less than min budget",
        function (value) {
          const { min_budget } = this.parent;
          if (!value || !min_budget) return true;
          const minBudget = parseFloat(min_budget);
          const maxBudget = parseFloat(value);
          if (isNaN(minBudget) || isNaN(maxBudget)) return true;
          return maxBudget >= minBudget;
        }
      );
  } else {
    shape.min_budget = Yup.string()
      .nullable()
      .test(
        "min-budget-validation",
        "Min budget cannot be greater than max budget",
        function (value) {
          const { max_budget } = this.parent;
          if (!value || !max_budget) return true;
          const minBudget = parseFloat(value);
          const maxBudget = parseFloat(max_budget);
          if (isNaN(minBudget) || isNaN(maxBudget)) return true;
          return minBudget <= maxBudget;
        }
      );

    shape.max_budget = Yup.string()
      .nullable()
      .test(
        "max-budget-validation",
        "Max budget cannot be less than min budget",
        function (value) {
          const { min_budget } = this.parent;
          if (!value || !min_budget) return true;
          const minBudget = parseFloat(min_budget);
          const maxBudget = parseFloat(value);
          if (isNaN(minBudget) || isNaN(maxBudget)) return true;
          return maxBudget >= minBudget;
        }
      );
  }

  return Yup.object().shape(shape);
};

/**
 * Transforms lead details from API response to form values
 * Handles default values when leadDetails is null
 */
export const transformLeadDetailsToFormValues = (
  leadDetails: LeadDetails | null,
  transformedCountryOptions: CountryOption[],
  region?: string,
): FormValues => {
  if (!leadDetails) {
    const defaultCountry = findCountryByCode(
      getRegionFlag(region) || "GB",
      transformedCountryOptions,
    );
    return {
      name: "",
      email: "",
      phoneCountry: defaultCountry,
      phoneNumber: "",
      whatsappCountry: defaultCountry,
      whatsappNumber: "",
      isSameAsPhone: false,
      destinationCountry: null,
      destinationCity: null,
      university: null,
      roomType: [],
      levelOfStudy: null,
      tenantType: null,
      utmSource: null,
      utmMedium: null,
      choosenProps: [],
      tags: [],
      min_budget: "",
      max_budget: "",
      stay_duration: "",
      move_in_date: null,
      moveInSession: null,
      currentStage: null,
      howDidYouHearAboutUs: null,
      marketingConsent: null,
      assigned_agents: [],
    };
  }

  const phoneCountryCode =
    leadDetails.phone_country_code || leadDetails.country_code;
  const whatsappCountryCode =
    leadDetails.whatsapp_country_code || phoneCountryCode;

  const phoneCountry = phoneCountryCode
    ? findCountryByCode(
        phoneCountryCode.replace("+", ""),
        transformedCountryOptions,
      ) ||
      transformedCountryOptions.find((c) => c.dialCode === phoneCountryCode) ||
      null
    : findCountryByCode("GB", transformedCountryOptions);

  const whatsappCountry = whatsappCountryCode
    ? findCountryByCode(
        whatsappCountryCode.replace("+", ""),
        transformedCountryOptions,
      ) ||
      transformedCountryOptions.find(
        (c) => c.dialCode === whatsappCountryCode,
      ) ||
      null
    : phoneCountry;

  return {
    name: leadDetails.name || "",
    email: leadDetails.email || "",
    phoneCountry,
    phoneNumber: leadDetails.phone || "",
    whatsappCountry,
    whatsappNumber: leadDetails.whatsapp_phone || "",
    isSameAsPhone:
      leadDetails.phone === leadDetails.whatsapp_phone &&
      phoneCountryCode === whatsappCountryCode,
    destinationCountry:
      leadDetails.demographics?.destination_country_id?.toString() || null,
    destinationCity:
      leadDetails.demographics?.destination_city_id?.toString() || null,
    university: leadDetails.demographics?.university_id?.toString() || null,
    roomType: leadDetails.room_type || [],
    levelOfStudy: leadDetails.level_of_study || null,
    tenantType: leadDetails.tenant_type || null,
    utmSource: leadDetails.tracking_info?.utm_source || null,
    utmMedium: leadDetails.tracking_info?.utm_medium || null,
    choosenProps: (leadDetails.choosen_props || leadDetails.chosen_props)?.map((prop) => prop.property_id.toString()) || [],
    tags: leadDetails.tags?.map((tag) => tag.code || tag.value || "").filter(Boolean) || [],
    min_budget: leadDetails.budget?.min_budget?.toString() || "",
    max_budget: leadDetails.budget?.max_budget?.toString() || "",
    stay_duration: leadDetails.lease?.value?.toString() || "",
    move_in_date: leadDetails.move_in_date
      ? new Date(leadDetails.move_in_date)
      : null,
    moveInSession: (leadDetails as unknown as Record<string, unknown>)?.academic_year as string || null,
    currentStage: leadDetails.current_stage || null,
    howDidYouHearAboutUs: leadDetails.how_did_you_hear_about_us || null,
    marketingConsent: leadDetails.marketing_consent != null
      ? (leadDetails.marketing_consent ? "Yes" : "No")
      : null,
    assigned_agents: Array.isArray(leadDetails.agent)
      ? leadDetails.agent.map((a) => a.id)
      : [],
  };
};
